import React, { useMemo, useState } from 'react';
import { useSpring, animated, config } from 'react-spring';
import { useTrail } from 'react-spring';
import '../styles/Contact.css';
import { contactChannels } from '../data/projectData';
import { LOCALES, localizeText } from '../utils/localize';

const socialIconById = {
  github: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.2c3.26-.36 6.68-1.6 6.68-7.2a5.6 5.6 0 0 0-1.5-3.86 5.2 5.2 0 0 0-.09-3.86s-1.18-.36-3.86 1.48a13.38 13.38 0 0 0-7 0C5.55-.34 4.37.02 4.37.02a5.2 5.2 0 0 0-.09 3.86 5.6 5.6 0 0 0-1.5 3.86c0 5.56 3.42 6.84 6.68 7.2a4.8 4.8 0 0 0-1 3.2v4" />
      <path d="M9 18c-4.51 2-5-2-7-2" />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37a4 4 0 1 1-4.63-4.63 4 4 0 0 1 4.63 4.63z" />
      <line x1="17.5" y1="6.5" x2="17.5" y2="6.5" />
    </svg>
  ),
};

const contactHeaderIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 14a5 5 0 0 1 0-7l1.8-1.8a5 5 0 1 1 7 7L17 14" />
    <path d="M14 10a5 5 0 0 1 0 7L12.2 18.8a5 5 0 1 1-7-7L7 10" />
  </svg>
);

const formatUrl = (href) => href.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

const Contact = ({ locale = LOCALES.KR }) => {
  const L = (text) => localizeText(text, locale);
  const [activeChannelId, setActiveChannelId] = useState(null);

  const goToTop = () => {
    const section = document.getElementById('home');

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const containerSpring = useSpring({
    from: { opacity: 0, y: 50 },
    to: { opacity: 1, y: 0 },
    config: config.slow,
  });

  const titleSpring = useSpring({
    from: { opacity: 0, y: -20 },
    to: { opacity: 1, y: 0 },
    config: config.default,
  });

  const trail = useTrail(contactChannels.length, {
    from: { opacity: 0, scale: 0.8 },
    to: { opacity: 1, scale: 1 },
    config: config.default,
  });

  const activeChannel = useMemo(
    () => contactChannels.find((channel) => channel.id === activeChannelId) ?? null,
    [activeChannelId],
  );

  return (
    <section className="contact" id="contact">
      <animated.div style={containerSpring} className="contact-container section-shell">
        <div className="section-header contact-header">
          <animated.h2 style={titleSpring} className="section-title">
            <span className="contact-heading-icon" aria-hidden="true">{contactHeaderIcon}</span>
            <span>Contact Me</span>
          </animated.h2>
        </div>

        <div className="contact-grid" onMouseLeave={() => setActiveChannelId(null)}>
          {trail.map((animatedStyle, index) => {
            const channel = contactChannels[index];

            return (
              <animated.a
                key={channel.title}
                href={channel.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`contact-link ${activeChannelId === channel.id ? 'is-active' : ''}`}
                onMouseEnter={() => setActiveChannelId(channel.id)}
                onFocus={() => setActiveChannelId(channel.id)}
                onBlur={() => setActiveChannelId(null)}
                style={{
                  ...animatedStyle,
                  '--contact-accent-rgb': channel.accentRgb,
                }}
              >
                <div className="contact-link-head">
                  <div className="contact-link-mark" aria-hidden="true">
                    {socialIconById[channel.id]}
                  </div>
                  <div className="contact-link-title-wrap">
                    <span className="contact-link-title">{channel.title}</span>
                    <span className="contact-link-accent" aria-hidden="true" />
                  </div>
                </div>
                <span className="contact-link-url">{formatUrl(channel.href)}</span>
              </animated.a>
            );
          })}
        </div>
        <p
          className={`contact-event ${activeChannel ? 'is-active' : ''}`}
          aria-live="polite"
          style={activeChannel ? { '--contact-accent-rgb': activeChannel.accentRgb } : undefined}
        >
          {activeChannel ? `LINK READY :: ${activeChannel.title.toUpperCase()}` : 'HOVER A LINK'}
        </p>

        <div className="contact-top-arrow-wrap">
          <button className="contact-top-arrow" type="button" onClick={goToTop} aria-label={L('맨 위로 이동')}>
            <span className="contact-top-arrow-head" />
          </button>
        </div>
      </animated.div>
    </section>
  );
};

export default Contact;
